(() => {
  const initSlider = (slider) => {
    if (slider.dataset.bfHomeSliderReady === 'true') return;

    const track = slider.querySelector('[data-bf-slider-track]');
    const dots = slider.querySelector('[data-bf-slider-dots]');
    const nav = slider.querySelector('[data-bf-slider-nav]');
    const prev = slider.querySelector('[data-bf-slider-prev]');
    const next = slider.querySelector('[data-bf-slider-next]');

    if (!track || !dots || !prev || !next) return;

    slider.dataset.bfHomeSliderReady = 'true';

    const isCenterSlider = slider.hasAttribute('data-bf-slider-center');
    const isLoopSlider = slider.hasAttribute('data-bf-slider-loop');
    const isMobileLoopSlider = slider.hasAttribute('data-bf-slider-mobile-loop');
    const mobileLoopQuery = window.matchMedia('(max-width: 749px)');
    const isLoopEnabled = () => isLoopSlider || (isMobileLoopSlider && mobileLoopQuery.matches);
    const getSlides = () => Array.from(track.querySelectorAll(':scope > .bf-home-slider__slide'));
    const isClone = (slide) => slide.hasAttribute('data-bf-slider-clone');
    const getRealSlides = () => getSlides().filter((slide) => !isClone(slide));
    const getSlideIndex = (slide, fallbackIndex) => {
      const index = Number.parseInt(slide.dataset.bfSlideIndex || '', 10);
      return Number.isNaN(index) ? fallbackIndex : index;
    };
    const getPaddingStart = () => parseFloat(window.getComputedStyle(track).paddingInlineStart) || 0;
    const makeGeneratedClone = (slide) => {
      const clone = slide.cloneNode(true);
      clone.classList.add('bf-home-slider__slide--clone');
      clone.setAttribute('data-bf-slider-clone', 'true');
      clone.setAttribute('data-bf-slider-generated-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');

      if (clone.matches('a, button, input, select, textarea, [tabindex]')) {
        clone.setAttribute('tabindex', '-1');
      }

      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });

      return clone;
    };

    const syncGeneratedLoopClones = () => {
      if (!isMobileLoopSlider) return;

      track.querySelectorAll('[data-bf-slider-generated-clone]').forEach((clone) => {
        clone.remove();
      });

      if (!isLoopEnabled()) return;

      const slides = getRealSlides();
      if (slides.length < 2) return;

      track.insertBefore(makeGeneratedClone(slides[slides.length - 1]), slides[0]);
      track.appendChild(makeGeneratedClone(slides[0]));
    };

    const getClosestSlide = () => {
      const slides = getSlides();
      if (!slides.length) return { slide: null, index: 0 };

      const targetPosition = isCenterSlider
        ? track.scrollLeft + track.clientWidth / 2
        : track.scrollLeft + getPaddingStart();

      let closestSlide = null;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const slidePosition = isCenterSlider
          ? slide.offsetLeft + slide.offsetWidth / 2
          : slide.offsetLeft;
        const distance = Math.abs(slidePosition - targetPosition);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestSlide = slide;
          closestIndex = getSlideIndex(slide, index);
        }
      });

      return { slide: closestSlide, index: closestIndex };
    };

    const getCurrentIndex = () => getClosestSlide().index;

    let loopResetTimer = null;

    const scrollToElement = (slide, behavior = 'smooth') => {
      const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
      const left = isCenterSlider
        ? slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2
        : slide.offsetLeft - getPaddingStart();

      track.scrollTo({
        left: Math.min(Math.max(left, 0), maxScroll),
        behavior
      });
    };

    const getLoopCloneTarget = (requestedIndex, nextIndex) => {
      if (!isLoopEnabled()) return null;

      const realSlides = getRealSlides();
      if (!realSlides.length) return null;

      const currentIndex = getCurrentIndex();
      const lastIndex = realSlides.length - 1;
      const allSlides = getSlides();
      const firstRealSlide = realSlides[0];
      const lastRealSlide = realSlides[lastIndex];

      if (currentIndex === 0 && requestedIndex < 0) {
        return allSlides
          .filter((slide) => isClone(slide) && getSlideIndex(slide, 0) === nextIndex && slide.offsetLeft < firstRealSlide.offsetLeft)
          .pop() || null;
      }

      if (currentIndex === lastIndex && requestedIndex > lastIndex) {
        return allSlides
          .find((slide) => isClone(slide) && getSlideIndex(slide, 0) === nextIndex && slide.offsetLeft > lastRealSlide.offsetLeft) || null;
      }

      return null;
    };

    const scheduleLoopReset = (index) => {
      if (loopResetTimer) window.clearTimeout(loopResetTimer);
      loopResetTimer = window.setTimeout(() => {
        scrollToSlide(index, 'auto');
      }, 500);
    };

    const scrollToSlide = (index, behavior = 'smooth') => {
      const slides = getRealSlides();
      if (!slides.length) return;

      const nextIndex = (index + slides.length) % slides.length;
      const loopCloneTarget = behavior !== 'auto' ? getLoopCloneTarget(index, nextIndex) : null;
      const slide = loopCloneTarget || slides.find((candidate, candidateIndex) => getSlideIndex(candidate, candidateIndex) === nextIndex) || slides[nextIndex];

      scrollToElement(slide, behavior);

      if (loopCloneTarget) {
        scheduleLoopReset(nextIndex);
      }
    };

    const update = () => {
      const currentIndex = getCurrentIndex();

      dots.querySelectorAll('button').forEach((dot, index) => {
        dot.setAttribute('aria-current', index === currentIndex ? 'true' : 'false');
      });

      /* Nothing was ever disabled here, so at either end, and on a track whose
         slides all fit, the arrows silently did nothing and read as broken.
         A looping slider can always move, so it keeps both enabled. */
      const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
      const scrollable = maxScroll > 1;
      const looping = isLoopEnabled();

      if (nav) {
        /* Set inline rather than through a class: the shared sheet gives the
           nav display:flex, which would win over the hidden attribute. */
        nav.style.display = scrollable || looping ? '' : 'none';
      }

      prev.disabled = !looping && track.scrollLeft <= 1;
      next.disabled = !looping && track.scrollLeft >= maxScroll - 1;
    };

    const rebuildDots = () => {
      syncGeneratedLoopClones();

      const pageCount = getRealSlides().length;
      const currentDots = dots.querySelectorAll('button').length;

      if (currentDots !== pageCount) {
        dots.textContent = '';
        for (let index = 0; index < pageCount; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'bf-home-slider__dot';
          dot.setAttribute('aria-label', `Go to slide group ${index + 1}`);
          dot.addEventListener('click', () => {
            scrollToSlide(index);
          });
          dots.appendChild(dot);
        }
      }

      update();

      if (isLoopEnabled() && slider.dataset.bfLoopPositioned !== 'true') {
        slider.dataset.bfLoopPositioned = 'true';
        scrollToSlide(0, 'auto');
      } else if (!isLoopEnabled()) {
        delete slider.dataset.bfLoopPositioned;
      }
    };

    prev.addEventListener('click', () => {
      scrollToSlide(getCurrentIndex() - 1);
    });

    next.addEventListener('click', () => {
      scrollToSlide(getCurrentIndex() + 1);
    });

    let frame = null;
    let loopSettleTimer = null;

    const settleLoopClone = () => {
      if (!isLoopEnabled()) return;

      const closest = getClosestSlide();
      if (closest.slide && isClone(closest.slide)) {
        scrollToSlide(closest.index, 'auto');
      }
    };

    track.addEventListener('scroll', () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);

      if (loopSettleTimer) window.clearTimeout(loopSettleTimer);
      loopSettleTimer = window.setTimeout(settleLoopClone, 140);
    }, { passive: true });

    if ('ResizeObserver' in window) {
      new ResizeObserver(rebuildDots).observe(track);
    } else {
      window.addEventListener('resize', rebuildDots);
    }

    rebuildDots();
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-bf-home-slider]').forEach(initSlider);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
